import React, { Dispatch, SetStateAction } from 'react';
import { Intl } from '@erecruit/core';
import { Resources, ListParams, Mutations } from '@erecruit/types';
import {
  createSearchPage,
  FilterHeaderValuesTemplate,
  BaseSearchProps,
  createRequestParams,
  OnDateRangeFilter,
  ResetDateFilter,
  FilterHandler,
  createColumnsHook,
  SortOrder,
} from '@erecruit/modules';
import { connect } from 'react-redux';
import { Moment } from 'moment';
import { searchShifts, selectors, exportShifts } from './state/reducer';
import {
  ShiftColumnType,
  generateColumns,
  defaultVisibleColumns,
  parseTableFilters,
  getDefinedShiftReportColumns,
} from './columns';

type ShiftParams = 'All' | 'Filled' | 'UnFilled' | 'Cancelled';

const { FormattedMessage } = Intl;

const filterHeaderValues: FilterHeaderValuesTemplate<ShiftParams>[] = [
  {
    value: 'All',
    label: <FormattedMessage id={messageIds.vms.shifts.all} />,
  },
  {
    value: 'Filled',
    icon: 'filled-shift',
    label: <FormattedMessage id={messageIds.vms.shifts.filled} />,
  },
  {
    value: 'UnFilled',
    icon: 'calendar',
    label: <FormattedMessage id={messageIds.vms.shifts.unfilled} />,
  },
  {
    value: 'Cancelled',
    icon: 'cancelled-shift',
    label: <FormattedMessage id={messageIds.vms.shifts.cancelled} />,
  },
];

export interface ShiftSearchFilterData {
  selectedDays: Moment[];
}

interface ShiftSearchProps
  extends BaseSearchProps<
      Resources.ShiftSearch,
      ShiftSearchFilterData,
      Resources.ShiftSearchFilter
    >,
    Intl.InjectedIntlProps {}

function getRequestParams(
  shiftSearchType: ShiftParams,
): ListParams<Resources.ShiftSearchFilter> {
  return createRequestParams(
    {
      shiftSearchType: Resources.ShiftSearchType[shiftSearchType],
      searchTerm: '',
    },
    nameof<Resources.ShiftSearch>(o => o.start),
    SortOrder.Asc,
  );
}

function getFilterImplementation(
  filterParams: Resources.ShiftSearchParams,
  setFilterParams: Dispatch<SetStateAction<Resources.ShiftSearchParams>>,
): Record<string, FilterHandler> {
  const filterByDate: OnDateRangeFilter = (dateFieldKey, dateRange) => {
    setFilterParams({
      ...filterParams,
      filter: { ...filterParams.filter, [dateFieldKey]: dateRange },
    });
  };

  const resetDateFilter: ResetDateFilter = dateFieldKey => {
    if (
      filterParams.filter &&
      !filterParams.filter[dateFieldKey as keyof Resources.ShiftSearchFilter]
    )
      return;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [dateFieldKey as keyof Resources.ShiftSearchFilter]: omit,
      ...other
    } = filterParams.filter as Resources.ShiftSearchFilter;

    setFilterParams({
      ...filterParams,
      filter: { ...other },
    });
  };

  return {
    filterByDate,
    resetDateFilter,
  };
}

function generateColumnsFactory(
  filters: Record<string, FilterHandler>,
  filterData: Resources.ShiftSearchFilter,
) {
  const filterByDate = filters['filterByDate'] as OnDateRangeFilter;
  const resetDateFilter = filters['resetDateFilter'] as ResetDateFilter;

  return generateColumns(filterByDate, resetDateFilter, filterData);
}

const useColumns = createColumnsHook(
  getFilterImplementation,
  generateColumnsFactory,
);

const getReportColumns = (
  intl: Intl.InjectedIntl,
  visibleColumns: string[],
) => {
  const allDefinedColumns = getDefinedShiftReportColumns(intl);
  const visibleColumnsIds = visibleColumns.map(
    visibleColumn => visibleColumn.split('.')[0],
  );
  let reportColumns = allDefinedColumns.filter(
    reportColumn =>
      visibleColumnsIds.findIndex(
        visibleColumnId => reportColumn.id === visibleColumnId,
      ) !== -1,
  );

  const startColumn: Mutations.ColumnDefinition = {
    id: nameof<Resources.ShiftSearch>(o => o.start),
    header: intl.formatMessage({ id: messageIds.vms.shiftSearch.start }),
  };

  const endColumn: Mutations.ColumnDefinition = {
    id: nameof<Resources.ShiftSearch>(o => o.end),
    header: intl.formatMessage({ id: messageIds.vms.shiftSearch.end }),
  };

  // if shift time exists, add Start and End implicitly
  if (
    visibleColumnsIds.findIndex(
      visibleColumn => visibleColumn === 'shiftTime',
    ) !== -1
  ) {
    // start was added as separate column, append
    const startColumnIndex = visibleColumnsIds.findIndex(
      visibleColumn =>
        visibleColumn === nameof<Resources.ShiftSearch>(o => o.start),
    );

    if (startColumnIndex !== -1) {
      // append just 'End' column just after 'Start' column
      reportColumns.splice(startColumnIndex, 0, endColumn);
    } else {
      // append both 'Start' and 'End' columns to end
      reportColumns = [...reportColumns, ...[startColumn, endColumn]];
    }
  }

  return reportColumns;
};

const ShiftSearchPage = createSearchPage<
  Resources.ShiftSearchFilter,
  ShiftSearchProps,
  ShiftColumnType,
  ShiftParams,
  Resources.ShiftSearch,
  ShiftSearchFilterData
>({
  title: <FormattedMessage id={messageIds.vms.shifts.shifts} />,
  defaultSearchQuery: 'All',
  searchTermPlaceholderText: 'Search by candidate',
  baseQueryRoute: 'shiftType',
  alwaysVisibleColumns: [nameof<Resources.ShiftSearch>(o => o.id), 'actions'],
  rowKey: nameof<Resources.ShiftSearch>(o => o.id),
  filterHeaderValues: filterHeaderValues,
  useColumns,
  getRequestParams,
  getReportColumns,
  getDefaultVisibleColumns: () => defaultVisibleColumns,
  parseFilterParams: (filters, columns, filterParams) => {
    const parsedFilters = parseTableFilters(filters, columns);
    return {
      ...parsedFilters,
      shiftSearchType: filterParams?.shiftSearchType,
      searchTerm: filterParams?.searchTerm,
    };
  },
});

export default connect(selectors.selectShiftSearchState, {
  searchEntities: searchShifts,
  exportAction: exportShifts,
})(ShiftSearchPage);
